import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
  Alert,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  MapPin,
  Users,
  ArrowLeft,
  Package,
  AlertTriangle,
  TrendingDown,
  Phone,
  Mail,
  User,
  Save,
  X,
  History,
  Activity,
} from "lucide-react";
import {
  getAllMagasins,
  getMagasinById,
  createMagasin,
  updateMagasin,
  deleteMagasin,
  getMagasinStocks,
  getMagasinsByManager
} from "../api/magasins";
import {
  addStock,
  removeStock,
  adjustStock,
  updateStockThresholds,
  getMovementsByMagasin,
  getMovementsByProduct,
} from "../api/stock";
import { getAllProducts } from "../api/products";
import { getUsersByRole } from "../api/users";
import useAuth from "../hooks/useAuth";
import useForm from "../hooks/useForm";
import { getAllZones } from "../api/zones";
import { useLocation } from "react-router-dom";

const MagasinManagement = () => {
  const { user, isAdmin } = useAuth();

  // États principaux
  const [view, setView] = useState("list"); // 'list', 'detail', 'form', 'stocks'
  const [magasins, setMagasins] = useState([]);
  const [selectedMagasin, setSelectedMagasin] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // États pour les filtres et recherche
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState("all");
  const [filteredMagasins, setFilteredMagasins] = useState([]);

  // États pour les modals de stock
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [zones, setZones] = useState([]);

  // Formulaires
  const [thresholdForm, setThresholdForm] = useState({
    minQuantity: 0,
    maxQuantity: 0,
  });

  // Modal "Ajouter un mouvement" (entrée / sortie / ajustement)
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [movementForm, setMovementForm] = useState({
    type: "STOCK_IN",
    productId: "",
    quantity: 1,
    newQuantity: 0,
    notes: "",
  });

  // Modal "Mouvements du produit"
  const [showProductMovementsModal, setShowProductMovementsModal] = useState(false);
  const [productMovements, setProductMovements] = useState([]);
  const [productMovementsTitle, setProductMovementsTitle] = useState("");
  const [productMovementsLoading, setProductMovementsLoading] = useState(false);

  // Formulaire magasin avec useForm hook
  const initialValues = {
    name: "",
    code: "",
    address: "",
    city: "",
    phoneNumber: "",
    email: "",
    zoneId: "", // ← AJOUTER
    managerId: "",
    active: true,
  };

  const { values, handleChange, setValues, errors, validateForm, resetForm } =
    useForm(initialValues);

  useEffect(() => {
    if (!user) return;
    fetchMagasins();
    fetchProducts();
    fetchManagers();
    fetchZones();   
}, [user]);

  useEffect(() => {
    filterMagasins();
  }, [searchTerm, filterActive, magasins]);

  const fetchMagasins = async () => {
    try {
        setLoading(true);
        const data = isAdmin
            ? await getAllMagasins()
            : await getMagasinsByManager(user.id);   // gestionnaire : seulement ses magasins
        setMagasins(Array.isArray(data) ? data : []);
        setError('');
    } catch (err) {
        setError('Erreur lors du chargement des magasins');
        console.error(err);
        setMagasins([]);
    } finally {
        setLoading(false);
    }
};

  const fetchProducts = async () => {
    try {
      const data = await getAllProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur lors du chargement des produits:", err);
      setProducts([]); // ✅ Initialiser à tableau vide
    }
  };

  const fetchZones = async () => {
    try {
      const data = await getAllZones();
      setZones(Array.isArray(data) ? data : []);
      console.log('Toutes les zone',data);
      
    } catch (err) {
      console.error("Erreur lors du chargement des zones:", err);
      setZones([]);
    }
  };

  const fetchManagers = async () => {
    try {
      const users = await getUsersByRole("MANAGER");
      setManagers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error("Erreur lors du chargement des gestionnaires:", err);
      setManagers([]); // ✅ Initialiser à tableau vide
    }
  };

  const fetchMagasinDetails = async (id) => {
    try {
      setLoading(true);
      const data = await getMagasinById(id);
      setSelectedMagasin(data);
      const stocksData = await getMagasinStocks(id);
      setStocks(Array.isArray(stocksData) ? stocksData : []); // ✅ Vérifier que c'est un tableau

      // Historique des mouvements (isolé : un échec ici ne casse pas l'affichage des stocks)
      try {
        const movementsData = await getMovementsByMagasin(id);
        const sorted = (Array.isArray(movementsData) ? movementsData : [])
          .sort((a, b) => new Date(b.movementDate) - new Date(a.movementDate));
        setMovements(sorted);
      } catch (mErr) {
        console.error("Erreur lors du chargement des mouvements:", mErr);
        setMovements([]);
      }
    } catch (err) {
      setError("Erreur lors du chargement des détails");
      setStocks([]); // ✅ Initialiser à tableau vide
      setMovements([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterMagasins = () => {
    let filtered = magasins;

    if (filterActive === "active") {
      filtered = filtered.filter((m) => m.active);
    } else if (filterActive === "inactive") {
      filtered = filtered.filter((m) => !m.active);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (m) =>
          m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.city.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredMagasins(filtered);
  };

  const handleViewDetails = (magasin) => {
    setSelectedMagasin(magasin);
    fetchMagasinDetails(magasin.id);
    setView("detail");
  };

  const handleCreateNew = () => {
    resetForm();
    setSelectedMagasin(null);
    setView("form");
  };

  const handleEdit = (magasin) => {
    setSelectedMagasin(magasin);
    setValues({
      name: magasin.name || "",
      code: magasin.code || "",
      address: magasin.address || "",
      city: magasin.city || "",
      phoneNumber: magasin.phoneNumber || "",
      email: magasin.email || "",
      zoneId: magasin.zoneId || "", // ← AJOUTER
      managerId: magasin.managerId || "",
      active: magasin.active ?? true,
    });
    setView("form");
  };

  const handleDelete = async (id, name) => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer le magasin "${name}" ?`,
      )
    ) {
      try {
        await deleteMagasin(id);
        setSuccess("Magasin supprimé avec succès");
        fetchMagasins();
      } catch (err) {
        setError(
          err.response?.data?.message || "Erreur lors de la suppression",
        );
      }
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    const validationSchema = {
      name: { required: true, errorMessage: "Le nom est requis" },
      code: { required: true, errorMessage: "Le code est requis" },
      address: { required: true, errorMessage: "L'adresse est requise" },
      city: { required: true, errorMessage: "La ville est requise" },
    };

    if (!validateForm(validationSchema)) {
      return;
    }

    try {
      setLoading(true);
      const magasinData = {
        ...values,
        managerId: values.managerId ? Number(values.managerId) : null,
      };

      if (selectedMagasin) {
        await updateMagasin(selectedMagasin.id, magasinData);
        setSuccess("Magasin mis à jour avec succès");
      } else {
        await createMagasin(magasinData);
        setSuccess("Magasin créé avec succès");
      }

      fetchMagasins();
      setTimeout(() => {
        setView("list");
        resetForm();
      }, 1500);
    } catch (err) {
      setError(
        err.response?.data?.message || "Erreur lors de l'enregistrement",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManageStocks = (magasin) => {
    setSelectedMagasin(magasin);
    fetchMagasinDetails(magasin.id);
    setView("stocks");
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedMagasin(null);
    resetForm();
  };

  const location = useLocation();
  useEffect(() => {
    handleBackToList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ===== Gestion des stocks =====

  // Ouvrir le modal "Ajouter un mouvement" (optionnellement pré-rempli sur un produit)
  const openMovementModal = (productId = "") => {
    let nq = 0;
    if (productId) {
      const s = stocks.find((st) => String(st.productId) === String(productId));
      nq = s ? s.quantity : 0;
    }
    setMovementForm({
      type: "STOCK_IN",
      productId: productId ? String(productId) : "",
      quantity: 1,
      newQuantity: nq,
      notes: "",
    });
    setShowMovementModal(true);
  };

  // Soumettre un mouvement selon son type
  const handleSubmitMovement = async () => {
    if (!movementForm.productId) {
      setError("Veuillez sélectionner un produit.");
      return;
    }
    const pid = Number(movementForm.productId);
    try {
      if (movementForm.type === "STOCK_IN") {
        if (parseInt(movementForm.quantity) <= 0) {
          setError("La quantité doit être positive.");
          return;
        }
        await addStock(selectedMagasin.id, pid, parseInt(movementForm.quantity), user?.id, movementForm.notes);
      } else if (movementForm.type === "STOCK_OUT") {
        if (parseInt(movementForm.quantity) <= 0) {
          setError("La quantité doit être positive.");
          return;
        }
        await removeStock(selectedMagasin.id, pid, parseInt(movementForm.quantity), user?.id, movementForm.notes);
      } else {
        if (parseInt(movementForm.newQuantity) < 0) {
          setError("La quantité ne peut pas être négative.");
          return;
        }
        await adjustStock(selectedMagasin.id, pid, parseInt(movementForm.newQuantity), user?.id, movementForm.notes);
      }
      setSuccess("Mouvement enregistré avec succès");
      setShowMovementModal(false);
      fetchMagasinDetails(selectedMagasin.id);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement du mouvement");
    }
  };

  // Voir l'historique des mouvements d'un produit (tous magasins)
  const openProductMovements = async (stock) => {
    setProductMovementsTitle(`${stock.productName} (${stock.productSku})`);
    setProductMovements([]);
    setShowProductMovementsModal(true);
    setProductMovementsLoading(true);
    try {
      const data = await getMovementsByProduct(stock.productId);
      const sorted = (Array.isArray(data) ? data : [])
        .sort((a, b) => new Date(b.movementDate) - new Date(a.movementDate));
      setProductMovements(sorted);
    } catch (err) {
      console.error("Erreur chargement mouvements produit:", err);
      setProductMovements([]);
    } finally {
      setProductMovementsLoading(false);
    }
  };

  const handleUpdateThresholds = async () => {
    try {
      await updateStockThresholds(
        selectedMagasin.id,
        selectedStock.productId,
        parseInt(thresholdForm.minQuantity),
        parseInt(thresholdForm.maxQuantity) || null,
      );
      setSuccess("Seuils mis à jour avec succès");
      setShowThresholdModal(false);
      setThresholdForm({ minQuantity: 0, maxQuantity: 0 });
      setSelectedStock(null);
      fetchMagasinDetails(selectedMagasin.id);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la mise à jour");
    }
  };

  const openThresholdModal = (stock) => {
    setSelectedStock(stock);
    setThresholdForm({
      minQuantity: stock.minQuantity || 10,
      maxQuantity: stock.maxQuantity || 0,
    });
    setShowThresholdModal(true);
  };

  const getLowStockProducts = () => stocks.filter((s) => s.isLowStock);
  const getOutOfStockProducts = () => stocks.filter((s) => s.isOutOfStock);

  // ========== RENDER VIEWS ==========

  // Vue Liste
  // Boutons d'action réutilisés (tableau + cartes)
const renderMagasinActions = (magasin) => (
  <div className="d-flex gap-2 justify-content-end">
    <Button
      variant="outline-primary"
      size="sm"
      onClick={() => handleViewDetails(magasin)}
      title="Voir les détails"
    >
      <Eye size={16} />
    </Button>
    {isAdmin && (
      <>
        <Button
          variant="outline-warning"
          size="sm"
          onClick={() => handleEdit(magasin)}
          title="Modifier"
        >
          <Edit size={16} />
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleDelete(magasin.id, magasin.name)}
          title="Supprimer"
        >
          <Trash2 size={16} />
        </Button>
      </>
    )}
  </div>
);

// Vue Liste
const renderListView = () => (
  <>
    <Row className="mb-4">
      <Col>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h2 className="mb-1">Gestion des Magasins</h2>
            <p className="text-muted mb-0">
              Liste de tous les magasins ({filteredMagasins.length})
            </p>
          </div>
          {isAdmin && (
            <Button variant="primary" onClick={handleCreateNew}>
              <Plus size={18} className="me-2" />
              Nouveau Magasin
            </Button>
          )}
        </div>
      </Col>
    </Row>

    <Row className="mb-3 g-2">
      <Col xs={12} md={6}>
        <InputGroup>
          <InputGroup.Text>
            <Search size={18} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Rechercher par nom, code ou ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </Col>
      <Col xs={12} md={3}>
        <Form.Select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
        >
          <option value="all">Tous les magasins</option>
          <option value="active">Actifs uniquement</option>
          <option value="inactive">Inactifs uniquement</option>
        </Form.Select>
      </Col>
    </Row>

    {filteredMagasins.length === 0 ? (
      <Card className="shadow-sm">
        <Card.Body>
          <div className="text-center py-5">
            <MapPin size={48} className="text-muted mb-3" />
            <p className="text-muted mb-0">Aucun magasin trouvé</p>
          </div>
        </Card.Body>
      </Card>
    ) : (
      <>
        {/* ≥ lg : tableau */}
        <Card className="shadow-sm d-none d-lg-block">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Code</th>
                  <th>Nom</th>
                  <th>Ville</th>
                  <th>Zone</th>
                  <th>Gestionnaire</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMagasins.map((magasin) => (
                  <tr key={magasin.id}>
                    <td><strong>{magasin.code}</strong></td>
                    <td>{magasin.name}</td>
                    <td>
                      <MapPin size={14} className="me-1" />
                      {magasin.city}
                    </td>
                    <td>
                      {magasin.zoneName ? (
                        <Badge bg="primary">{magasin.zoneName}</Badge>
                      ) : (
                        <span className="text-muted">Aucune zone</span>
                      )}
                    </td>
                    <td>
                      {magasin.managerName ? (
                        <div className="d-flex align-items-center">
                          <Users size={14} className="me-1" />
                          {magasin.managerName}
                        </div>
                      ) : (
                        <span className="text-muted">Non assigné</span>
                      )}
                    </td>
                    <td>
                      <Badge bg={magasin.active ? "success" : "secondary"}>
                        {magasin.active ? "Actif" : "Inactif"}
                      </Badge>
                    </td>
                    <td>{renderMagasinActions(magasin)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* < lg : cartes */}
        <div className="d-lg-none">
          {filteredMagasins.map((magasin) => (
            <Card key={magasin.id} className="mb-2 shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <div className="fw-bold">{magasin.name}</div>
                    <small className="text-muted">{magasin.code}</small>
                  </div>
                  <Badge bg={magasin.active ? "success" : "secondary"}>
                    {magasin.active ? "Actif" : "Inactif"}
                  </Badge>
                </div>

                <div className="small mb-1 d-flex align-items-center">
                  <MapPin size={14} className="me-1 text-muted" />
                  {magasin.city}
                </div>

                <div className="small mb-1">
                  <span className="text-muted">Zone : </span>
                  {magasin.zoneName ? (
                    <Badge bg="primary">{magasin.zoneName}</Badge>
                  ) : (
                    <span className="text-muted">Aucune</span>
                  )}
                </div>

                <div className="small mb-3">
                  <span className="text-muted">Gestionnaire : </span>
                  {magasin.managerName || (
                    <span className="text-muted">Non assigné</span>
                  )}
                </div>

                {renderMagasinActions(magasin)}
              </Card.Body>
            </Card>
          ))}
        </div>
      </>
    )}
  </>
);

  // Vue Formulaire
  const renderFormView = () => (
    <>
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <Button variant="outline-secondary" onClick={handleBackToList}>
              <ArrowLeft size={18} />
            </Button>
            <h2 className="mb-0">
              {selectedMagasin ? "Modifier le Magasin" : "Nouveau Magasin"}
            </h2>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={8} className="mx-auto">
          <Card className="shadow-sm">
            <Card.Body>
              <Form onSubmit={handleSubmitForm}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nom du Magasin *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        placeholder="Ex: Magasin Dakar Centre"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Code *</Form.Label>
                      <Form.Control
                        type="text"
                        name="code"
                        value={values.code}
                        onChange={handleChange}
                        isInvalid={!!errors.code}
                        placeholder="Ex: MAG-DKR-01"
                        disabled={selectedMagasin}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.code}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Adresse *</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={values.address}
                    onChange={handleChange}
                    isInvalid={!!errors.address}
                    placeholder="Ex: Rue 10, Plateau"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.address}
                  </Form.Control.Feedback>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ville *</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={values.city}
                        onChange={handleChange}
                        isInvalid={!!errors.city}
                        placeholder="Ex: Dakar"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.city}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Téléphone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phoneNumber"
                        value={values.phoneNumber}
                        onChange={handleChange}
                        placeholder="Ex: +221 33 XXX XX XX"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        placeholder="Ex: magasin@soplast.com"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Gestionnaire</Form.Label>
                      <Form.Select
                        name="managerId"
                        value={values.managerId}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionner un gestionnaire</option>
                        {managers.map((manager) => (
                          <option key={manager.id} value={manager.id}>
                            {manager.fullName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Zone</Form.Label>
                      <Form.Select
                        name="zoneId"
                        value={values.zoneId}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionner une zone</option>
                        {zones.map((zone) => (
                          <option key={zone.id} value={zone.id}>
                            {zone.name} ({zone.code})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    name="active"
                    label="Magasin actif"
                    checked={values.active}
                    onChange={(e) =>
                      handleChange({
                        target: { name: "active", value: e.target.checked },
                      })
                    }
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleBackToList}
                    disabled={loading}
                  >
                    <X size={18} className="me-2" />
                    Annuler
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    <Save size={18} className="me-2" />
                    {loading ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  // Vue Détails
  const renderDetailView = () => {
  if (!selectedMagasin) return null;
 
  return (
    <>
      {/* En-tête : empile en colonne sur mobile, repasse en ligne dès md */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <Button variant="outline-secondary" onClick={handleBackToList}>
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h2 className="h4 mb-1">{selectedMagasin.name}</h2>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Badge bg={selectedMagasin.active ? "success" : "secondary"}>
                    {selectedMagasin.active ? "Actif" : "Inactif"}
                  </Badge>
                  <span className="text-muted small">
                    Code: {selectedMagasin.code}
                  </span>
                </div>
              </div>
            </div>
 
            {/* Sur mobile ces boutons prennent toute la largeur du bloc */}
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="primary"
                className="flex-grow-1 flex-md-grow-0"
                onClick={() => handleManageStocks(selectedMagasin)}
              >
                <Package size={18} className="me-2" />
                Gérer les Stocks
              </Button>
              {isAdmin && (
                <Button
                  variant="warning"
                  className="flex-grow-1 flex-md-grow-0"
                  onClick={() => handleEdit(selectedMagasin)}
                >
                  <Edit size={18} className="me-2" />
                  Modifier
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>
 
      {/* Cartes statistiques : empilées sous md, 3 colonnes à partir de md */}
      <Row className="mb-4 g-3">
        <Col xs={12} md={4}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                  <Package size={24} className="text-primary" />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Produits en Stock</h6>
                  <h3 className="mb-0">{stocks.length}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-warning bg-opacity-10 p-3 rounded me-3">
                  <AlertTriangle size={24} className="text-warning" />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Stock Bas</h6>
                  <h3 className="mb-0">{getLowStockProducts().length}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-danger bg-opacity-10 p-3 rounded me-3">
                  <TrendingDown size={24} className="text-danger" />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Rupture de Stock</h6>
                  <h3 className="mb-0">{getOutOfStockProducts().length}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
 
      <Card className="shadow-sm">
        <Card.Body>
          <Tabs defaultActiveKey="info" className="mb-3">
            <Tab eventKey="info" title="Informations">
              {/* g-3 pour l'espacement vertical quand les colonnes s'empilent */}
              <Row className="g-3">
                <Col md={6}>
                  <h5 className="mb-3">Informations Générales</h5>
                  <Table borderless responsive>
                    <tbody>
                      <tr>
                        <td className="text-muted" width="40%">
                          <MapPin size={16} className="me-2" />
                          Adresse
                        </td>
                        <td>
                          <strong>{selectedMagasin.address}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">
                          <MapPin size={16} className="me-2" />
                          Ville
                        </td>
                        <td>
                          <strong>{selectedMagasin.city}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">
                          <MapPin size={16} className="me-2" />
                          Zone
                        </td>
                        <td>
                          <strong>
                            {selectedMagasin.zoneName ? (
                              <Badge bg="primary">
                                {selectedMagasin.zoneName}
                              </Badge>
                            ) : (
                              <span className="text-muted">Aucune zone</span>
                            )}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">
                          <Phone size={16} className="me-2" />
                          Téléphone
                        </td>
                        <td>
                          <strong>
                            {selectedMagasin.phoneNumber || "Non renseigné"}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">
                          <Mail size={16} className="me-2" />
                          Email
                        </td>
                        <td>
                          <strong>
                            {selectedMagasin.email || "Non renseigné"}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
                <Col md={6}>
                  <h5 className="mb-3">Gestion</h5>
                  <Table borderless responsive>
                    <tbody>
                      <tr>
                        <td className="text-muted" width="40%">
                          <User size={16} className="me-2" />
                          Gestionnaire
                        </td>
                        <td>
                          <strong>
                            {selectedMagasin.managerName || (
                              <span className="text-muted">Non assigné</span>
                            )}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Date de création</td>
                        <td>
                          <strong>
                            {new Date(
                              selectedMagasin.createdAt,
                            ).toLocaleDateString("fr-FR")}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Dernière modification</td>
                        <td>
                          <strong>
                            {new Date(
                              selectedMagasin.updatedAt,
                            ).toLocaleDateString("fr-FR")}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Tab>
 
            <Tab eventKey="stocks" title={`Stocks (${stocks.length})`}>
              {stocks.length === 0 ? (
                <div className="text-center py-4">
                  <Package size={48} className="text-muted mb-3" />
                  <p className="text-muted">Aucun produit en stock</p>
                  <Button
                    variant="primary"
                    onClick={() => handleManageStocks(selectedMagasin)}
                  >
                    Ajouter des produits
                  </Button>
                </div>
              ) : (
                <Table responsive hover>
                  <thead className="bg-light">
                    <tr>
                      <th>Produit</th>
                      <th>SKU</th>
                      <th>Quantité</th>
                      <th>Seuil Min</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stocks.map((stock) => (
                      <tr key={stock.id}>
                        <td>
                          <strong>{stock.productName}</strong>
                        </td>
                        <td>{stock.productSku}</td>
                        <td>
                          <Badge
                            bg={
                              stock.isOutOfStock
                                ? "danger"
                                : stock.isLowStock
                                  ? "warning"
                                  : "success"
                            }
                          >
                            {stock.quantity}
                          </Badge>
                        </td>
                        <td>{stock.minQuantity}</td>
                        <td>
                          {stock.isOutOfStock ? (
                            <Badge bg="danger">Rupture</Badge>
                          ) : stock.isLowStock ? (
                            <Badge bg="warning" text="dark">
                              Stock bas
                            </Badge>
                          ) : (
                            <Badge bg="success">OK</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Tab>
 
            <Tab
              eventKey="alerts"
              title={`Alertes (${getLowStockProducts().length + getOutOfStockProducts().length})`}
            >
              {getLowStockProducts().length === 0 &&
              getOutOfStockProducts().length === 0 ? (
                <div className="text-center py-4">
                  <AlertTriangle size={48} className="text-success mb-3" />
                  <p className="text-muted">Aucune alerte de stock</p>
                </div>
              ) : (
                <>
                  {getOutOfStockProducts().length > 0 && (
                    <>
                      <h5 className="text-danger mb-3">
                        <TrendingDown size={20} className="me-2" />
                        Rupture de Stock ({getOutOfStockProducts().length})
                      </h5>
                      <Table responsive hover className="mb-4">
                        <thead className="bg-danger bg-opacity-10">
                          <tr>
                            <th>Produit</th>
                            <th>SKU</th>
                            <th>Seuil Min</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getOutOfStockProducts().map((stock) => (
                            <tr key={stock.id}>
                              <td>
                                <strong>{stock.productName}</strong>
                              </td>
                              <td>{stock.productSku}</td>
                              <td>{stock.minQuantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
 
                  {getLowStockProducts().length > 0 && (
                    <>
                      <h5 className="text-warning mb-3">
                        <AlertTriangle size={20} className="me-2" />
                        Stock Bas ({getLowStockProducts().length})
                      </h5>
                      <Table responsive hover>
                        <thead className="bg-warning bg-opacity-10">
                          <tr>
                            <th>Produit</th>
                            <th>SKU</th>
                            <th>Quantité</th>
                            <th>Seuil Min</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getLowStockProducts().map((stock) => (
                            <tr key={stock.id}>
                              <td>
                                <strong>{stock.productName}</strong>
                              </td>
                              <td>{stock.productSku}</td>
                              <td>
                                <Badge bg="warning" text="dark">
                                  {stock.quantity}
                                </Badge>
                              </td>
                              <td>{stock.minQuantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </>
  );
};

  // Vue Gestion Stocks
  const renderStocksView = () => {
  if (!selectedMagasin) return null;
 
  // Groupe d'actions réutilisé par le tableau (lg+) ET les cartes (mobile)
  const renderStockActions = (stock) => (
    <div className="d-flex gap-2 justify-content-end flex-wrap">
      <Button
        variant="outline-info"
        size="sm"
        onClick={() => openProductMovements(stock)}
        title="Voir les mouvements du produit"
      >
        <History size={16} />
      </Button>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => openMovementModal(stock.productId)}
        title="Ajouter un mouvement"
      >
        <Activity size={16} />
      </Button>
      <Button
        variant="outline-warning"
        size="sm"
        onClick={() => openThresholdModal(stock)}
        title="Seuils"
      >
        <AlertTriangle size={16} />
      </Button>
    </div>
  );
 
  const renderStockStatus = (stock) =>
    stock.isOutOfStock ? (
      <Badge bg="danger">
        <AlertTriangle size={14} className="me-1" />
        Rupture
      </Badge>
    ) : stock.isLowStock ? (
      <Badge bg="warning" text="dark">
        <AlertTriangle size={14} className="me-1" />
        Stock bas
      </Badge>
    ) : (
      <Badge bg="success">OK</Badge>
    );
 
  return (
    <>
      {/* En-tête : empile en colonne sur mobile */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <Button
                variant="outline-secondary"
                onClick={() => setView("detail")}
              >
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h2 className="h4 mb-1">Gestion des Stocks</h2>
                <p className="text-muted mb-0 small">
                  {selectedMagasin.name} - {stocks.length} produit(s)
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={() => openMovementModal()}
            >
              <Plus size={18} className="me-2" />
              Ajouter un mouvement
            </Button>
          </div>
        </Col>
      </Row>
 
      {stocks.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body>
            <div className="text-center py-5">
              <Package size={48} className="text-muted mb-3" />
              <p className="text-muted">Aucun produit en stock</p>
              <Button
                variant="primary"
                onClick={() => openMovementModal()}
              >
                Ajouter des produits
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* ≥ lg : tableau complet */}
          <Card className="shadow-sm d-none d-lg-block">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Produit</th>
                    <th>SKU</th>
                    <th>Quantité</th>
                    <th>Seuil Min</th>
                    <th>Seuil Max</th>
                    <th>Statut</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.map((stock) => (
                    <tr key={stock.id}>
                      <td>
                        <strong>{stock.productName}</strong>
                      </td>
                      <td>{stock.productSku}</td>
                      <td>
                        <Badge
                          bg={
                            stock.isOutOfStock
                              ? "danger"
                              : stock.isLowStock
                                ? "warning"
                                : "success"
                          }
                        >
                          {stock.quantity}
                        </Badge>
                      </td>
                      <td>{stock.minQuantity}</td>
                      <td>{stock.maxQuantity || "-"}</td>
                      <td>{renderStockStatus(stock)}</td>
                      <td>{renderStockActions(stock)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
 
          {/* < lg : cartes empilées */}
          <div className="d-lg-none">
            {stocks.map((stock) => (
              <Card key={stock.id} className="mb-2 shadow-sm">
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="fw-bold">{stock.productName}</div>
                      <small className="text-muted">{stock.productSku}</small>
                    </div>
                    {renderStockStatus(stock)}
                  </div>
 
                  <div className="d-flex flex-wrap gap-3 small mb-3">
                    <div>
                      <span className="text-muted">Quantité : </span>
                      <Badge
                        bg={
                          stock.isOutOfStock
                            ? "danger"
                            : stock.isLowStock
                              ? "warning"
                              : "success"
                        }
                      >
                        {stock.quantity}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-muted">Seuil min : </span>
                      {stock.minQuantity}
                    </div>
                    <div>
                      <span className="text-muted">Seuil max : </span>
                      {stock.maxQuantity || "-"}
                    </div>
                  </div>
 
                  {renderStockActions(stock)}
                </Card.Body>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Historique des mouvements du magasin (entrées, sorties, ajustements, livraisons) */}
      <Card className="shadow-sm mt-4">
        <Card.Header className="bg-white d-flex align-items-center">
          <History size={18} className="me-2 text-muted" />
          <h5 className="mb-0">Historique des mouvements</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {movements.length === 0 ? (
            <div className="text-center text-muted py-4">
              Aucun mouvement enregistré pour ce magasin.
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Produit</th>
                    <th className="text-center">Quantité</th>
                    <th>Origine</th>
                    <th>Par</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => {
                    const meta = {
                      STOCK_IN: { label: "Entrée", bg: "success" },
                      STOCK_OUT: { label: "Sortie", bg: "danger" },
                      ADJUSTMENT: { label: "Ajustement", bg: "info" },
                      RETURN: { label: "Retour", bg: "secondary" },
                    }[m.type] || { label: m.type, bg: "secondary" };
                    const up =
                      m.type === "STOCK_OUT"
                        ? false
                        : m.type === "ADJUSTMENT"
                          ? m.quantity >= 0
                          : true;
                    const qtyColor = up ? "success" : "danger";
                    return (
                      <tr key={m.id}>
                        <td className="small">
                          {m.movementDate
                            ? new Date(m.movementDate).toLocaleString("fr-FR", {
                                day: "2-digit", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </td>
                        <td><Badge bg={meta.bg}>{meta.label}</Badge></td>
                        <td>
                          <span className="fw-semibold">{m.productName}</span>
                          {m.productSku && (
                            <small className="text-muted"> ({m.productSku})</small>
                          )}
                        </td>
                        <td className={`text-center fw-bold text-${qtyColor}`}>
                          {up ? "+" : "−"}{Math.abs(m.quantity)}
                        </td>
                        <td className="small">
                          {m.orderNumber
                            ? `Commande ${m.orderNumber}`
                            : (m.reference || "Manuel")}
                          {m.notes && <div className="text-muted">{m.notes}</div>}
                        </td>
                        <td className="small">{m.performedByName || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

  // ========== RENDER PRINCIPAL ==========

  if (loading && view === "list") {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {view === "list" && renderListView()}
      {view === "form" && renderFormView()}
      {view === "detail" && renderDetailView()}
      {view === "stocks" && renderStocksView()}

      {/* Modals pour gestion des stocks */}

      {/* Modal Ajouter un mouvement (entrée / sortie / ajustement) */}
      <Modal show={showMovementModal} onHide={() => setShowMovementModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter un mouvement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Type de mouvement *</Form.Label>
              <Form.Select
                value={movementForm.type}
                onChange={(e) => {
                  const type = e.target.value;
                  let nq = movementForm.newQuantity;
                  if (type === "ADJUSTMENT" && movementForm.productId) {
                    const s = stocks.find((st) => String(st.productId) === String(movementForm.productId));
                    nq = s ? s.quantity : 0;
                  }
                  setMovementForm({ ...movementForm, type, newQuantity: nq });
                }}
              >
                <option value="STOCK_IN">Entrée</option>
                <option value="STOCK_OUT">Sortie</option>
                <option value="ADJUSTMENT">Ajustement d'inventaire</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Magasin</Form.Label>
              <Form.Control
                type="text"
                value={selectedMagasin ? `${selectedMagasin.name} (${selectedMagasin.code})` : ""}
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Produit *</Form.Label>
              <Form.Select
                value={movementForm.productId}
                onChange={(e) => {
                  const pid = e.target.value;
                  let nq = movementForm.newQuantity;
                  if (movementForm.type === "ADJUSTMENT") {
                    const s = stocks.find((st) => String(st.productId) === String(pid));
                    nq = s ? s.quantity : 0;
                  }
                  setMovementForm({ ...movementForm, productId: pid, newQuantity: nq });
                }}
              >
                <option value="">Sélectionner un produit</option>
                {(movementForm.type === "STOCK_IN"
                  ? products
                  : products.filter((p) =>
                      stocks.some((s) => Number(s.productId) === Number(p.id)),
                    )
                ).map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} - {product.sku}
                  </option>
                ))}
              </Form.Select>
              {movementForm.productId && (
                <Form.Text className="text-muted">
                  Stock actuel :{" "}
                  {(() => {
                    const s = stocks.find((st) => String(st.productId) === String(movementForm.productId));
                    return s ? s.quantity : 0;
                  })()}
                </Form.Text>
              )}
            </Form.Group>

            {movementForm.type === "ADJUSTMENT" ? (
              <Form.Group className="mb-3">
                <Form.Label>Nouvelle quantité réelle *</Form.Label>
                <Form.Control
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  min="0"
                  value={movementForm.newQuantity}
                  onChange={(e) => setMovementForm({ ...movementForm, newQuantity: e.target.value })}
                />
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Quantité *</Form.Label>
                <Form.Control
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  min="1"
                  value={movementForm.quantity}
                  onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>{movementForm.type === "ADJUSTMENT" ? "Motif *" : "Motif / Notes"}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={movementForm.notes}
                onChange={(e) => setMovementForm({ ...movementForm, notes: e.target.value })}
                placeholder={
                  movementForm.type === "ADJUSTMENT"
                    ? "Ex : inventaire physique, casse, correction…"
                    : movementForm.type === "STOCK_IN"
                      ? "Ex : réception fournisseur, retour, transfert…"
                      : "Ex : sortie exceptionnelle, perte, transfert…"
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMovementModal(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitMovement}
            disabled={
              !movementForm.productId ||
              (movementForm.type !== "ADJUSTMENT" && movementForm.quantity <= 0)
            }
          >
            <Save size={16} className="me-1" /> Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Mouvements du produit */}
      <Modal
        show={showProductMovementsModal}
        onHide={() => setShowProductMovementsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <History size={18} className="me-2" />
            Mouvements — {productMovementsTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {productMovementsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : productMovements.length === 0 ? (
            <div className="text-center text-muted py-4">
              Aucun mouvement pour ce produit.
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm" className="align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Magasin</th>
                    <th className="text-center">Qté</th>
                    <th>Origine</th>
                    <th>Par</th>
                  </tr>
                </thead>
                <tbody>
                  {productMovements.map((m) => {
                    const meta = {
                      STOCK_IN: { label: "Entrée", bg: "success" },
                      STOCK_OUT: { label: "Sortie", bg: "danger" },
                      ADJUSTMENT: { label: "Ajustement", bg: "info" },
                      RETURN: { label: "Retour", bg: "secondary" },
                    }[m.type] || { label: m.type, bg: "secondary" };
                    const up =
                      m.type === "STOCK_OUT"
                        ? false
                        : m.type === "ADJUSTMENT"
                          ? m.quantity >= 0
                          : true;
                    const qtyColor = up ? "success" : "danger";
                    return (
                      <tr key={m.id}>
                        <td className="small">
                          {m.movementDate
                            ? new Date(m.movementDate).toLocaleString("fr-FR", {
                                day: "2-digit", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </td>
                        <td><Badge bg={meta.bg}>{meta.label}</Badge></td>
                        <td className="small">{m.magasinName}</td>
                        <td className={`text-center fw-bold text-${qtyColor}`}>
                          {up ? "+" : "−"}{Math.abs(m.quantity)}
                        </td>
                        <td className="small">
                          {m.orderNumber
                            ? `Commande ${m.orderNumber}`
                            : (m.reference || "Manuel")}
                          {m.notes && <div className="text-muted">{m.notes}</div>}
                        </td>
                        <td className="small">{m.performedByName || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProductMovementsModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Seuils */}
      <Modal
        show={showThresholdModal}
        onHide={() => setShowThresholdModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Modifier les Seuils</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStock && (
            <>
              <p className="mb-3">
                <strong>Produit:</strong> {selectedStock.productName}
              </p>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Seuil Minimum *</Form.Label>
                  <Form.Control
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    min="0"
                    value={thresholdForm.minQuantity}
                    onChange={(e) =>
                      setThresholdForm({
                        ...thresholdForm,
                        minQuantity: e.target.value,
                      })
                    }
                  />
                  <Form.Text className="text-muted">
                    Alerte quand le stock descend en dessous de ce seuil
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Seuil Maximum (optionnel)</Form.Label>
                  <Form.Control
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    min="0"
                    value={thresholdForm.maxQuantity}
                    onChange={(e) =>
                      setThresholdForm({
                        ...thresholdForm,
                        maxQuantity: e.target.value,
                      })
                    }
                  />
                  <Form.Text className="text-muted">
                    Laisser vide si aucun maximum
                  </Form.Text>
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowThresholdModal(false)}
          >
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateThresholds}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MagasinManagement;